import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { UserGroupIcon, TrashIcon, LinkIcon } from '@heroicons/react/24/outline';

type Role = 'owner' | 'editor' | 'viewer';

interface Billing {
  ownerPays: boolean;
  ownerCreditCap: number | null;
  useOwnerCredits: boolean;
  usedThisMonth: number;
}

interface Member {
  id: string;
  userId: string;
  name: string | null;
  email?: string | null;
  role: 'editor' | 'viewer';
  /** Present for the owner, and for the collaborator's own row. */
  billing?: Billing;
}

interface Invite {
  id: string;
  email: string;
  role: 'editor' | 'viewer';
  ownerPays: boolean;
  ownerCreditCap: number | null;
  expiresAt: string;
}

/** A monthly-limit field: blank is no limit, otherwise 1,000+ words (the API's minimum). */
const parseCap = (raw: string): number | null | undefined => {
  const v = raw.trim().replace(/[,\s]/g, '');
  if (!v) return null;
  const n = Number(v);
  return Number.isInteger(n) && n >= 1_000 && n <= 10_000_000 ? n : undefined;
};

interface MembersResponse {
  myRole: Role;
  owner: { userId: string; name: string | null; email?: string | null };
  members: Member[];
  invites: Invite[];
}

const fieldStyle = { borderColor: 'var(--ds-border)', color: 'var(--ds-text-primary)' };

/** Who's on the story; the owner invites, changes roles and removes people here. */
const StoryPeople: React.FC<{ storyId: string; currentUserId?: string }> = ({
  storyId,
  currentUserId,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState<MembersResponse | null>(null);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'editor' | 'viewer'>('editor');
  const [sending, setSending] = useState(false);
  const [lastLink, setLastLink] = useState<{ email: string; link: string } | null>(null);
  const [needsPlan, setNeedsPlan] = useState(false);
  const [ownerPays, setOwnerPays] = useState(false);
  const [capInput, setCapInput] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`/stories/${storyId}/members`);
      setData(res.data);
    } catch (err) {
      console.error('Failed to load collaborators:', err);
    }
  }, [storyId]);

  useEffect(() => {
    load();
  }, [load]);

  const roleLabel = (r: Role) =>
    r === 'owner'
      ? t('stories.members.roleOwner', 'Owner')
      : r === 'editor'
        ? t('stories.members.roleEditor', 'Editor')
        : t('stories.members.roleViewer', 'Viewer');

  const copy = async (link: string) => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success(t('stories.members.linkCopied', 'Invite link copied'));
    } catch {
      window.prompt(t('stories.members.copyPrompt', 'Copy the invite link:'), link);
    }
  };

  const sendInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    const cap = role === 'editor' && ownerPays ? parseCap(capInput) : null;
    if (cap === undefined) {
      toast.error(t('stories.members.capInvalid', 'Enter at least 1,000 words, or leave it blank'));
      return;
    }
    setSending(true);
    try {
      const res = await axios.post(`/stories/${storyId}/invites`, {
        email: email.trim(),
        role,
        ownerPays: role === 'editor' && ownerPays,
        ...(cap ? { ownerCreditCap: cap } : {}),
      });
      setLastLink({ email: res.data.invite.email, link: res.data.link });
      toast.success(
        res.data.emailSent
          ? t('stories.members.inviteSent', 'Invite sent to {{email}}', {
              email: res.data.invite.email,
            })
          : t(
              'stories.members.inviteNoEmail',
              "Invite created, but the email didn't send. Copy the link and send it yourself.",
            ),
      );
      setEmail('');
      load();
    } catch (err: any) {
      if (err?.response?.data?.code === 'plan_required') {
        setNeedsPlan(true);
      } else {
        toast.error(
          err?.response?.data?.message || t('stories.members.inviteError', 'Could not send invite'),
        );
      }
    } finally {
      setSending(false);
    }
  };

  const updateMember = async (
    member: Member,
    changes: { role?: 'editor' | 'viewer'; ownerPays?: boolean; ownerCreditCap?: number | null },
  ) => {
    try {
      await axios.patch(`/stories/${storyId}/members/${member.id}`, changes);
      load();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || t('stories.saveError', 'Failed to save'));
    }
  };

  const saveCap = (member: Member, raw: string) => {
    const cap = parseCap(raw);
    if (cap === undefined) {
      toast.error(t('stories.members.capInvalid', 'Enter at least 1,000 words, or leave it blank'));
      return;
    }
    if (cap !== (member.billing?.ownerCreditCap ?? null))
      updateMember(member, { ownerCreditCap: cap });
  };

  const chooseWords = async (useOwnerCredits: boolean) => {
    try {
      await axios.patch(`/stories/${storyId}/billing`, { useOwnerCredits });
      load();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || t('stories.saveError', 'Failed to save'));
    }
  };

  const remove = async (member: Member) => {
    const leaving = member.userId === currentUserId;
    const ok = window.confirm(
      leaving
        ? t('stories.members.confirmLeave', 'Leave this story? You will lose access to it.')
        : t('stories.members.confirmRemove', 'Remove {{name}} from this story?', {
            name: member.name || member.email || '',
          }),
    );
    if (!ok) return;
    try {
      await axios.delete(`/stories/${storyId}/members/${member.id}`);
      if (leaving) {
        navigate('/stories');
        return;
      }
      load();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || t('stories.saveError', 'Failed to save'));
    }
  };

  const revoke = async (invite: Invite) => {
    try {
      await axios.delete(`/stories/${storyId}/invites/${invite.id}`);
      if (lastLink?.email === invite.email) setLastLink(null);
      load();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || t('stories.saveError', 'Failed to save'));
    }
  };

  if (!data) return null;
  const isOwner = data.myRole === 'owner';

  return (
    <div
      className="ds-card rounded-2xl p-5 space-y-3"
      style={{ background: 'var(--ds-bg-card)', border: '1px solid var(--ds-border)' }}
    >
      <div className="flex items-center gap-2">
        <UserGroupIcon className="w-5 h-5 text-primary-400" />
        <h3 className="text-sm font-semibold" style={{ color: 'var(--ds-text-primary)' }}>
          {t('stories.members.title', 'People')}
        </h3>
      </div>

      <ul className="space-y-2">
        <li className="flex items-center justify-between gap-2 text-sm">
          <span className="min-w-0 truncate" style={{ color: 'var(--ds-text-primary)' }}>
            {data.owner.name || data.owner.email || t('stories.members.unnamed', 'Unnamed writer')}
            {data.owner.userId === currentUserId && (
              <span style={{ color: 'var(--ds-text-secondary)' }}>
                {' '}
                ({t('stories.members.you', 'you')})
              </span>
            )}
          </span>
          <span className="text-xs shrink-0" style={{ color: 'var(--ds-text-secondary)' }}>
            {roleLabel('owner')}
          </span>
        </li>
        {data.members.map((m) => (
          <li key={m.id} className="space-y-1">
            <div className="flex items-center justify-between gap-2 text-sm">
              <span className="min-w-0 truncate" style={{ color: 'var(--ds-text-primary)' }}>
                {m.name || m.email || t('stories.members.unnamed', 'Unnamed writer')}
                {m.userId === currentUserId && (
                  <span style={{ color: 'var(--ds-text-secondary)' }}>
                    {' '}
                    ({t('stories.members.you', 'you')})
                  </span>
                )}
              </span>
              <span className="flex items-center gap-2 shrink-0">
                {isOwner ? (
                  <select
                    value={m.role}
                    onChange={(e) =>
                      updateMember(m, { role: e.target.value as 'editor' | 'viewer' })
                    }
                    className="pl-2 pr-7 py-1 rounded-lg border bg-transparent text-xs"
                    style={fieldStyle}
                    aria-label={t('stories.members.roleLabel', 'Role')}
                  >
                    <option value="editor">{roleLabel('editor')}</option>
                    <option value="viewer">{roleLabel('viewer')}</option>
                  </select>
                ) : (
                  <span className="text-xs" style={{ color: 'var(--ds-text-secondary)' }}>
                    {roleLabel(m.role)}
                  </span>
                )}
                {(isOwner || m.userId === currentUserId) && (
                  <button
                    onClick={() => remove(m)}
                    aria-label={
                      m.userId === currentUserId
                        ? t('stories.members.leave', 'Leave story')
                        : t('stories.members.remove', 'Remove')
                    }
                    title={
                      m.userId === currentUserId
                        ? t('stories.members.leave', 'Leave story')
                        : t('stories.members.remove', 'Remove')
                    }
                  >
                    <TrashIcon className="w-4 h-4 text-red-400" />
                  </button>
                )}
              </span>
            </div>
            {isOwner && m.billing && m.role === 'editor' && (
              <div
                className="flex flex-wrap items-center gap-x-3 gap-y-1 pl-3 text-xs"
                style={{ color: 'var(--ds-text-secondary)' }}
              >
                <label className="flex items-center gap-1.5">
                  <input
                    type="checkbox"
                    checked={m.billing.ownerPays}
                    onChange={(e) => updateMember(m, { ownerPays: e.target.checked })}
                  />
                  {t('stories.members.mayUseMine', 'May use my AI words')}
                </label>
                {m.billing.ownerPays && (
                  <>
                    <label className="flex items-center gap-1.5">
                      {t('stories.members.monthlyLimit', 'Monthly limit')}
                      <input
                        key={`${m.id}-${m.billing.ownerCreditCap ?? ''}`}
                        type="text"
                        inputMode="numeric"
                        defaultValue={m.billing.ownerCreditCap ?? ''}
                        placeholder={t('stories.members.noLimit', 'No limit')}
                        onBlur={(e) => saveCap(m, e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                        }}
                        className="w-24 px-2 py-0.5 rounded border bg-transparent"
                        style={fieldStyle}
                        aria-label={t('stories.members.monthlyLimit', 'Monthly limit')}
                      />
                    </label>
                    <span>
                      {t('stories.members.usedThisMonth', '{{used}} used this month', {
                        used: m.billing.usedThisMonth.toLocaleString(),
                      })}
                      {!m.billing.useOwnerCredits &&
                        ` · ${t('stories.members.usingOwn', 'they are using their own words')}`}
                    </span>
                  </>
                )}
              </div>
            )}
            {!isOwner && m.userId === currentUserId && m.billing && m.role === 'editor' && (
              <div className="pl-3 text-xs space-y-1" style={{ color: 'var(--ds-text-secondary)' }}>
                {m.billing.ownerPays ? (
                  <>
                    <div>{t('stories.members.whoseWords', 'AI words on this story:')}</div>
                    <label className="flex items-center gap-1.5">
                      <input
                        type="radio"
                        name={`words-${m.id}`}
                        checked={m.billing.useOwnerCredits}
                        onChange={() => chooseWords(true)}
                      />
                      {t('stories.members.useOwners', "Use {{name}}'s words", {
                        name: data.owner.name || t('stories.members.theOwner', 'the owner'),
                      })}
                      {m.billing.ownerCreditCap
                        ? ` (${t('stories.members.capUsage', '{{used}} of {{cap}} this month', {
                            used: m.billing.usedThisMonth.toLocaleString(),
                            cap: m.billing.ownerCreditCap.toLocaleString(),
                          })})`
                        : ''}
                    </label>
                    <label className="flex items-center gap-1.5">
                      <input
                        type="radio"
                        name={`words-${m.id}`}
                        checked={!m.billing.useOwnerCredits}
                        onChange={() => chooseWords(false)}
                      />
                      {t('stories.members.useMine', 'Use my own words')}
                    </label>
                  </>
                ) : (
                  <div>
                    {t('stories.members.ownWordsOnly', 'You use your own AI words on this story.')}
                  </div>
                )}
              </div>
            )}
          </li>
        ))}
      </ul>

      {isOwner && data.invites.length > 0 && (
        <div className="space-y-1">
          <div className="text-xs font-semibold" style={{ color: 'var(--ds-text-secondary)' }}>
            {t('stories.members.pending', 'Invited')}
          </div>
          {data.invites.map((inv) => (
            <div key={inv.id} className="flex items-center justify-between gap-2 text-sm">
              <span className="min-w-0 truncate" style={{ color: 'var(--ds-text-primary)' }}>
                {inv.email}
              </span>
              <span className="flex items-center gap-2 shrink-0">
                <span className="text-xs" style={{ color: 'var(--ds-text-secondary)' }}>
                  {roleLabel(inv.role)}
                  {inv.ownerPays && ` · ${t('stories.members.onMyWords', 'on my words')}`}
                </span>
                {lastLink?.email === inv.email && (
                  <button
                    onClick={() => copy(lastLink.link)}
                    aria-label={t('stories.members.copyLink', 'Copy invite link')}
                    title={t('stories.members.copyLink', 'Copy invite link')}
                  >
                    <LinkIcon className="w-4 h-4" style={{ color: 'var(--ds-text-secondary)' }} />
                  </button>
                )}
                <button
                  onClick={() => revoke(inv)}
                  className="text-xs text-red-400"
                  title={t('stories.members.revoke', 'Cancel invite')}
                >
                  {t('stories.members.revoke', 'Cancel invite')}
                </button>
              </span>
            </div>
          ))}
        </div>
      )}

      {isOwner && (
        <form onSubmit={sendInvite} className="space-y-2 pt-1">
          <div className="flex flex-wrap gap-2">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('stories.members.emailPlaceholder', 'Email address')}
              className="flex-1 min-w-[12rem] px-3 py-2 rounded-lg border bg-transparent text-sm"
              style={fieldStyle}
              required
            />
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as 'editor' | 'viewer')}
              className="pl-3 pr-8 py-2 rounded-lg border bg-transparent text-sm"
              style={fieldStyle}
              aria-label={t('stories.members.roleLabel', 'Role')}
            >
              <option value="editor">{roleLabel('editor')}</option>
              <option value="viewer">{roleLabel('viewer')}</option>
            </select>
            <button
              type="submit"
              disabled={sending || !email.trim()}
              className="px-4 py-2 rounded-lg bg-primary-500 hover:bg-primary-600 disabled:opacity-50 text-white text-sm font-medium"
            >
              {t('stories.members.invite', 'Invite')}
            </button>
          </div>
          {role === 'editor' && (
            <div
              className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs"
              style={{ color: 'var(--ds-text-secondary)' }}
            >
              <label className="flex items-center gap-1.5">
                <input
                  type="checkbox"
                  checked={ownerPays}
                  onChange={(e) => setOwnerPays(e.target.checked)}
                />
                {t('stories.members.letUseMine', 'Let them use my AI words')}
              </label>
              {ownerPays && (
                <label className="flex items-center gap-1.5">
                  {t('stories.members.monthlyLimit', 'Monthly limit')}
                  <input
                    type="text"
                    inputMode="numeric"
                    value={capInput}
                    onChange={(e) => setCapInput(e.target.value)}
                    placeholder={t('stories.members.noLimit', 'No limit')}
                    className="w-24 px-2 py-0.5 rounded border bg-transparent"
                    style={fieldStyle}
                  />
                </label>
              )}
            </div>
          )}
          <p className="text-xs" style={{ color: 'var(--ds-text-secondary)' }}>
            {t(
              'stories.members.roleHint',
              'Editors can write, edit and use the AI. Viewers can only read.',
            )}
          </p>
          {needsPlan && (
            <p className="text-xs" style={{ color: 'var(--ds-text-secondary)' }}>
              {t('stories.members.planRequired', 'Inviting collaborators needs a paid plan.')}{' '}
              <button
                type="button"
                onClick={() => navigate('/subscription')}
                className="text-primary-400 underline"
              >
                {t('stories.members.seePlans', 'See plans')}
              </button>
            </p>
          )}
        </form>
      )}
    </div>
  );
};

export default StoryPeople;
